import React, { FunctionComponent } from 'react';
import { useMediaQuery } from 'react-responsive';
import { Table } from 'components/UI/V3/Table/Table';
import { TableHeader } from 'components/UI/V3/Table/Header/TableHeader';
import { TableHeaderCell } from 'components/UI/V3/Table/HeaderCell/TableHeaderCell';
import { TableBody } from 'components/UI/V3/Table/Body/TableBody';
import { Service, Meta } from 'model/Service';
import WalkingServiceRow from './WalkingServiceRow';
import DefaultTableEmptyState from 'components/UI/V3/Table/EmptyState/DefaultTableEmptyState';
import Spinner from 'components/UI/Spinner';
import { DEFAULT_PAGE_SIZE } from 'hooks/useWalkingServices';
import styles from './WalkingServicesTable.module.scss';

interface WalkingServicesTableProps {
    services: Service[];
    meta: Meta | null;
    isLoading?: boolean;
    hasInitialized?: boolean;
    isEmpty?: boolean;
    onEdit?: (service: Service) => void;
    onDuplicate?: (service: Service) => void;
    onDeactivate?: (service: Service) => void;
    onDelete?: (service: Service) => void;
    onPageChange?: (page: number) => void;
    onPageSizeChange?: (size: number) => void;
    onRetry?: () => void;
    className?: string;
}

export const WalkingServicesTable: FunctionComponent<WalkingServicesTableProps> = ({
    services,
    meta,
    isLoading,
    hasInitialized,
    isEmpty,
    onEdit,
    onDuplicate,
    onDeactivate,
    onDelete,
    onPageChange,
    onPageSizeChange,
    onRetry,
    className
}) => {
    const isMobile = useMediaQuery({ query: '(max-width:768px)' });

    // Build pagination object from server meta
    // Always show pagination if we're on page 2+, regardless of total count
    const shouldShowPagination =
        meta &&
        (meta.currentPage > 1 || // Always show if not on first page
            meta.totalServices > DEFAULT_PAGE_SIZE); // Show if more results than page size

    const pagination = shouldShowPagination
        ? {
              page: meta!.currentPage,
              totalPages: meta!.totalPages,
              size: meta!.perPage,
              isLoading: isLoading || false
          }
        : undefined;

    // RENDER DIFFERENT TABLE BODIES BASED ON STATE
    const renderTableContent = () => {
        // Show spinner in TableBody when:
        // 1. First load (no data yet)
        // 2. Loading with no current data to show (empty results + loading)
        const shouldShowSpinnerInBody =
            isLoading &&
            (!hasInitialized || // First time loading
                services.length === 0); // Currently no data to show while loading

        if (shouldShowSpinnerInBody) {
            return (
                <TableBody>
                    <tr>
                        <td
                            colSpan={6}
                            style={{ textAlign: 'center', padding: '40px' }}
                        >
                            <Spinner />
                        </td>
                    </tr>
                </TableBody>
            );
        }

        // No results for current filters/search
        // Note: "Truly empty" case is handled at component level (parent), not here
        if (isEmpty) {
            return (
                <TableBody>
                    <DefaultTableEmptyState />
                </TableBody>
            );
        }

        // Normal data
        return (
            <TableBody>
                {services.map((service, index) => (
                    <WalkingServiceRow
                        key={service.id}
                        service={service}
                        isLast={services.length === index + 1}
                        isMobile={isMobile}
                        onEdit={onEdit}
                        onDuplicate={onDuplicate}
                        onDeactivate={onDeactivate}
                        onDelete={onDelete}
                    />
                ))}
            </TableBody>
        );
    };

    return (
        <Table
            pagination={pagination}
            onPageChange={onPageChange}
            onSizeChange={onPageSizeChange}
            className={className + ' ' + styles.table}
            fullHeight={true}
        >
            {!isMobile && (
                <TableHeader className={styles.tableHeader}>
                    <TableHeaderCell>Name</TableHeaderCell>
                    <TableHeaderCell>Staff</TableHeaderCell>
                    <TableHeaderCell>In-Home</TableHeaderCell>
                    <TableHeaderCell>Price</TableHeaderCell>
                    <TableHeaderCell>Taxes</TableHeaderCell>
                    <TableHeaderCell></TableHeaderCell>
                </TableHeader>
            )}
            {renderTableContent()}
        </Table>
    );
};

export default WalkingServicesTable;
